import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageDropdown } from "./LanguageDropdown";
import { NotFoundCard } from "./NotFoundCard";
import { LanguageCode } from "../api/contracts/language-settings";
import { getBrowserLanguageCode } from "../utils/browserLanguage";
import { I18N_LANGUAGES } from "../utils/language";
import i18n from "../lib/i18n";

/**
 * Validated against the languages the engine actually serves, rather than a
 * pair spelled out here: this page listed only sv-SE and en-GB, so a guest who
 * had chosen German or French landed on it in whatever their browser said.
 */
const readStoredLanguage = (): LanguageCode => {
  const stored = localStorage.getItem("selectedLanguage");
  if (stored && stored in I18N_LANGUAGES) return stored as LanguageCode;
  return getBrowserLanguageCode();
};

/**
 * Mounted in four places, two of which sit outside both the router and
 * HeaderProvider (the ConfigurationProvider error path and the Sentry
 * fallback). So this page owns its language state rather than reading
 * useHeader(), which throws without a provider. `useParams` is safe bare —
 * it falls back to an empty object when there is no route context.
 */
export function NotFound() {
  const { identifier } = useParams();
  const { t } = useTranslation();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] =
    useState<LanguageCode>(readStoredLanguage);

  const changeLanguage = (language: LanguageCode) => {
    setSelectedLanguage(language);
    localStorage.setItem("selectedLanguage", language);
    i18n.changeLanguage(I18N_LANGUAGES[language]);
  };

  const title = identifier
    ? t("error.not_found.site.title")
    : t("error.not_found.page.title");
  const description = identifier
    ? t("error.not_found.site.description", { identifier })
    : t("error.not_found.page.description");

  return (
    <div
      data-testid="not-found-page"
      className="flex min-h-screen flex-col bg-gradient-to-br from-surface-page to-surface-hover"
    >
      <div className="pl-8 pt-4">
        <div className="relative block">
          <button
            className="flex items-center gap-1 transition-opacity duration-200 hover:opacity-80"
            onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
          >
            <span className="text-xs font-medium text-ink/60">
              {t(`language.${selectedLanguage}`)}
            </span>
          </button>

          <LanguageDropdown
            isOpen={isLanguageDropdownOpen}
            onClose={() => setIsLanguageDropdownOpen(false)}
            selectedLanguage={selectedLanguage}
            onLanguageChange={changeLanguage}
          />
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <NotFoundCard title={title} description={description} />
      </div>
    </div>
  );
}
